import { Trans, useLingui } from "@lingui/react/macro";
import { useId, useState } from "react";

import { Button } from "@/shared/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { FRIENDS_METRICS, type FriendsMetric } from "../../model/FRIENDS_METRICS";
import type { FriendPlayer } from "../../model/FriendsBoard";
import { METRIC_HINTS, METRIC_LABELS } from "../../model/METRIC_LABELS";
import { placeMoves, rankPlayers } from "../../model/rankPlayers";
import { DuelPanel } from "./DuelPanel";
import { FriendRow, ROW_CLASSNAME } from "./FriendRow";
import { FriendsSection } from "./FriendsSection";

type FriendsTableProps = {
  me: FriendPlayer;
  friends: readonly FriendPlayer[];
  metric: FriendsMetric;
  onMetricChange: (metric: FriendsMetric) => void;
  // Якорь блока «Ещё не с нами» для кнопки «Позвать друзей».
  invitesAnchor: string | null;
  refreshing?: boolean;
};

// Таблица друзей по выбранному показателю. Нажатие на друга раскрывает сравнение один на один.
// Друзей в Relock нет — в таблице только ты и приглашение позвать их.
export const FriendsTable = ({
  me,
  friends,
  metric,
  onMetricChange,
  invitesAnchor,
  refreshing = false,
}: FriendsTableProps) => {
  const { i18n, t } = useLingui();
  const panelPrefix = useId();
  const [open, setOpen] = useState<number | null>(null);
  const players = [me, ...friends];
  const rows = rankPlayers(players, metric);
  const moves = placeMoves(players, metric);
  const values = rows.flatMap((row) => (row.value === null ? [] : [row.value]));
  const low = Math.min(...values);
  const high = Math.max(...values);
  const bar = (value: number | null) => {
    if (value === null) {
      return 0;
    }
    return high === low ? 100 : Math.round(15 + (85 * (value - low)) / (high - low));
  };

  return (
    <FriendsSection
      title={<Trans>Friends table</Trans>}
      refreshing={refreshing}
      aside={
        <ToggleGroup
          type="single"
          variant="pill"
          aria-label={t`Sort by`}
          value={metric}
          onValueChange={(value) => {
            const next = FRIENDS_METRICS.find((option) => option === value);
            if (next !== undefined) {
              onMetricChange(next);
            }
          }}
        >
          {FRIENDS_METRICS.map((option) => (
            <ToggleGroupItem key={option} value={option} className="min-h-9 px-3.5 text-sm">
              {i18n._(METRIC_LABELS[option])}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      }
    >
      <p className="text-[13px] text-subtle">
        {i18n._(METRIC_HINTS[metric])}{" "}
        {friends.length > 0 && <Trans>Tap a friend to compare yourself one on one.</Trans>}
      </p>
      {/* Узкий экран: строки прокручиваются вбок, а раскрытое сравнение — нет: оно шириной
          с видимую часть блока (`100cqw`) и прилипает к его левому краю. */}
      <div className="@container overflow-x-auto">
        <div className="flex min-w-[780px] flex-col gap-1.5">
          <div
            aria-hidden="true"
            className={`${ROW_CLASSNAME} px-3 text-xs tracking-[0.06em] text-subtle uppercase`}
          >
            <span>#</span>
            <span>
              <Trans>Player</Trans>
            </span>
            <span>
              <Trans>Rank</Trans>
            </span>
            <span>
              <Trans>Matches</Trans>
            </span>
            <span>{i18n._(METRIC_LABELS[metric])}</span>
            <span className="text-right">
              <Trans>Week</Trans>
            </span>
            <span />
          </div>
          {rows.map((row) => {
            const accountId = row.player.account_id;
            const isMe = accountId === me.account_id;
            const expanded = open === accountId;
            const panelId = `${panelPrefix}-${String(accountId)}`;
            return (
              <div key={accountId} className="flex flex-col gap-1.5">
                <FriendRow
                  player={row.player}
                  isMe={isMe}
                  place={row.place}
                  value={row.value}
                  metric={metric}
                  bar={bar(row.value)}
                  move={moves.get(accountId) ?? null}
                  expanded={expanded}
                  panelId={panelId}
                  onToggle={
                    isMe
                      ? undefined
                      : () => {
                          setOpen(expanded ? null : accountId);
                        }
                  }
                />
                {expanded && (
                  <div className="sticky left-0 w-[100cqw]">
                    <DuelPanel
                      id={panelId}
                      me={me}
                      them={row.player}
                      onClose={() => {
                        setOpen(null);
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {friends.length === 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-3.5 rounded-xl border border-dashed border-border-strong p-5">
              <div className="flex flex-[1_1_360px] flex-col gap-1">
                <p className="text-[17px] font-semibold">
                  <Trans>None of your friends are on Relock yet</Trans>
                </p>
                <p className="text-sm text-muted">
                  <Trans>
                    The table fills up as soon as a friend signs in with Steam. Below are friends
                    who played Deadlock in the last month.
                  </Trans>
                </p>
              </div>
              {invitesAnchor !== null && (
                <Button asChild>
                  <a href={`#${invitesAnchor}`}>
                    <Trans>Invite friends</Trans>
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </FriendsSection>
  );
};
